import { useMemo, useCallback, useContext } from 'react'
import PropTypes from 'prop-types'
import {
  flatMap,
  map,
  cloneDeep,
  includes,
  get,
  isNil,
  compact,
  uniq,
} from 'lodash'
import uuid from 'react-uuid'

import { TicketPropType } from '@common'
import { inputFactory, generateSubDocProps } from '@widget'

import { Feetables } from './feetables'
import { Benefits } from './benefits'
import { RequestVariables } from './variables'
import { AmontOptions } from './options'
import { rules } from './rules'
import { PlanContext } from './context'
import { planSchema, PlanPropType } from './schema'

import { checkPlan } from './common'

import { SchemaBaseEditor } from '../SchemaBaseEditor'
import { useCheckApi } from '../Validation'
import { useFetchFullDoc } from '../DocHelper'

import { _queryPlan } from './editor.gql'
import { extractOptions } from './dmBriefEditor'

export function PlanEditDialog(props) {
  const { doc, ticket } = props
  const { editContext, setEditContext } = useFetchFullDoc(
    doc,
    planSchema,
    _queryPlan,
  )

  const context = useContext(PlanContext)
  const checker = useCheckApi({ rules, context, externalAPI: checkPlan })

  const BenefitsWrapper = useCallback(
    props => <Benefits {...props} ticket={ticket} />,
    [ticket],
  )

  const amountOptionRefInBriefs = useMemo(
    () =>
      uniq(
        compact(
          flatMap(get(editContext, 'benefits'), benefit =>
            map(extractOptions(benefit.dm_brief), 'id'),
          ),
        ),
      ),
    [editContext],
  )

  const tabs = useMemo(
    () => [
      {
        caption: 'Basic',
        configs: [
          inputFactory(planSchema, 'name'),
          inputFactory(planSchema, 'is_additional', {
            inputAttributes: { width: '33%' },
          }),
          inputFactory(planSchema, 'benefit_restrict', {
            inputAttributes: {
              multilines: true,
            },
          }),
          inputFactory(planSchema, 'children_age_bound', {
            labelAttributes: {
              width: '300px',
            },
          }),
          inputFactory(planSchema, 'spouse_age_bound', {
            labelAttributes: {
              width: '300px',
            },
          }),
          inputFactory(planSchema, 'parent_age_bound', {
            labelAttributes: {
              width: '300px',
            },
          }),
          inputFactory(planSchema, 'assumed_interest', {
            labelAttributes: {
              width: '300px',
            },
          }),
        ],
      },
      {
        caption: planSchema.request_variables.label,
        configs: [
          {
            ...generateSubDocProps(planSchema, 'request_variables'),
            itemConfigs: [
              {
                widget: 'CustomizedInput',
                RenderComp: RequestVariables,
              },
            ],
            onClone: (context, index, insertDoc) => {
              insertDoc([
                {
                  ...cloneDeep(context[index]),
                  id: uuid(),
                },
              ])
            },
          },
        ],
      },
      {
        caption: planSchema.amount_options.label,
        configs: [
          {
            ...generateSubDocProps(planSchema, 'amount_options'),
            itemConfigs: [
              {
                widget: 'CustomizedInput',
                RenderComp: AmontOptions,
              },
            ],
            onCanDelete: ({ option_id }) =>
              !includes(
                [
                  ...map(
                    flatMap(get(editContext, 'feetables'), 'axes'),
                    'variable_id',
                  ),
                  ...map(get(editContext, 'benefits'), 'option_id'),
                  ...amountOptionRefInBriefs,
                ],
                option_id,
              ),
          },
        ],
      },
      {
        caption: 'Benefits',
        configs: [
          {
            fieldname: 'benefits',
            widget: 'CustomizedInput',
            RenderComp: BenefitsWrapper,
          },
        ],
      },
      {
        caption: 'Fee Tables',
        configs: [
          {
            fieldname: 'feetables',
            widget: 'CustomizedInput',
            RenderComp: Feetables,
          },
        ],
      },
    ],
    [editContext, BenefitsWrapper, amountOptionRefInBriefs],
  )

  return (
    <SchemaBaseEditor
      {...props}
      currentSchema={planSchema}
      tabs={tabs}
      checker={checker}
      isReady={!isNil(editContext)}
      editContext={editContext}
      setEditContext={setEditContext}
    />
  )
}

PlanEditDialog.propTypes = {
  doc: PlanPropType.isRequired,
  ticket: TicketPropType.isRequired,
  onHide: PropTypes.func.isRequired,
}
